import { inject } from '@angular/core'
import { CanMatchFn } from '@angular/router'
import { filter, map, of, switchMap, take, timeout } from 'rxjs'
import { CnectFeature, ConfigStore } from 'shared-lib'

/**
 * How long `canMatch` will wait for the remote-config-backed static config to
 * load before giving up and treating the feature as OFF. The fetch is kicked
 * off when `ConfigStore` is first constructed; on a normal navigation it has
 * usually resolved already, so this ceiling only bites on a cold hard-load (or
 * a failed/blocked fetch — see below).
 */
const CONFIG_LOAD_TIMEOUT_MS = 10_000

/**
 * Functional `CanMatch` guard factory. Returns a guard that lets the route
 * match only when the named CnectFeature is ON in `ConfigStore.features$`,
 * which is hydrated asynchronously from Firebase Remote Config (via
 * `@engineering11/config-web` → `RemoteConfigService`).
 *
 * IMPORTANT: that hydration is NOT awaited by any APP_INITIALIZER — the fetch
 * is fired from the `ConfigStore` constructor and resolves on its own clock.
 * So on a cold hard-load / deep-link, the router can evaluate this guard
 * BEFORE `features$` has a value. A naive `features$.pipe(take(1))` therefore
 * reads `undefined` → false → the real route is skipped and a sibling stub
 * fallback wins, even when the flag is ON. To avoid that race we first wait
 * for `staticConfigLoaded$` to flip true, THEN read the feature value.
 *
 * The fetch's error path (config-web) does NOT set `staticConfigLoaded`, so a
 * failed/blocked fetch would otherwise leave `canMatch` pending forever. The
 * `timeout` guards against that: if config never loads we emit `false`, which
 * preserves the safe default (feature treated as OFF → stub fallback).
 *
 * `take(1)` on each stage ensures the Observable completes (CanMatch contract).
 */
export const featureOnCanMatch =
  (feature: CnectFeature): CanMatchFn =>
  () => {
    const store = inject(ConfigStore)
    return store.staticConfigLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(() => store.features$.pipe(take(1))),
      map(features => !!features?.[feature]),
      timeout({ first: CONFIG_LOAD_TIMEOUT_MS, with: () => of(false) })
    )
  }
